import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { api } from "@/lib/api";

/**
 * Identidad efectiva de la sesión.
 *
 * Durante una suplantación el token sigue siendo el del administrador —el
 * backend resuelve la identidad del suplantado en cada petición—, así que la
 * interfaz NO puede dibujarse con los roles del token: mostraría las opciones
 * del administrador sobre los datos del suplantado. Este módulo es el único
 * lugar del frontend que decide qué roles rigen, y todo lo demás lo consulta
 * aquí.
 *
 * Fuera de una suplantación, los roles salen del token como siempre.
 */

export interface SuplantacionActual {
  activa: boolean;
  suplantacionId: string | null;
  suplantadoUsuarioId: string | null;
  suplantadoNombre: string | null;
  suplantadoCorreo: string | null;
  rolesEfectivos: string[];
  adminUsuarioId: string | null;
  adminCorreo: string | null;
  inicio: string | null;
}

const SIN_SUPLANTACION: SuplantacionActual = {
  activa: false,
  suplantacionId: null,
  suplantadoUsuarioId: null,
  suplantadoNombre: null,
  suplantadoCorreo: null,
  rolesEfectivos: [],
  adminUsuarioId: null,
  adminCorreo: null,
  inicio: null,
};

export function decodeJwt(token: string | undefined): Record<string, unknown> | null {
  if (!token) return null;
  try {
    return JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

/** Roles del token, sin considerar suplantación. */
export function rolesDelToken(token: string | undefined): string[] {
  const payload = decodeJwt(token);
  return ((payload?.realm_access as { roles?: string[] } | undefined)?.roles) ?? [];
}

export const SUPLANTACION_QUERY_KEY = ["suplantacion", "actual"] as const;

/** Estado de la suplantación de la sesión actual. */
export function useSuplantacion() {
  const auth = useAuth();
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: SUPLANTACION_QUERY_KEY,
    queryFn: async () => (await api.get<SuplantacionActual>("/api/suplantacion/actual")).data,
    enabled: !!auth.user?.access_token,
    // Se consulta seguido y cambia poco; lo que no puede es quedarse pegado
    // tras iniciar o salir, y eso se resuelve invalidando explícitamente.
    staleTime: 30_000,
  });
  return {
    suplantacion: query.data ?? SIN_SUPLANTACION,
    cargando: query.isLoading,
    refrescar: () => qc.invalidateQueries({ queryKey: SUPLANTACION_QUERY_KEY }),
  };
}

/**
 * Los roles que rigen la interfaz: los del usuario suplantado si hay una
 * suplantación viva, los del token si no.
 */
export function useRolesEfectivos(): string[] {
  const auth = useAuth();
  const { suplantacion } = useSuplantacion();
  if (suplantacion.activa) return suplantacion.rolesEfectivos;
  return rolesDelToken(auth.user?.access_token);
}

export function useTieneRol(...roles: string[]): boolean {
  const efectivos = useRolesEfectivos();
  return efectivos.some((r) => roles.includes(r));
}

/** Código de permiso funcional; el catálogo vive en kernel.permiso. */
export const PERMISO_SUBIR_CERTIFICADO_FIRMADO = "CERTIFICADO.SUBIR_FIRMADO";

/**
 * Permisos funcionales del usuario, tal como los resuelve el backend en
 * {@code /api/me} a partir de kernel.rol_permiso.
 *
 * Preferir esto a comprobar nombres de roles: así, ampliar quién puede hacer
 * algo es un cambio de datos y no exige tocar la interfaz. Como el backend los
 * calcula con la identidad efectiva, ya vienen bien durante una suplantación.
 */
export function usePermisos(): string[] {
  const auth = useAuth();
  const { suplantacion } = useSuplantacion();
  const query = useQuery({
    // La suplantación forma parte de la clave: al entrar o salir, los permisos
    // se recalculan en vez de quedarse con los de la identidad anterior.
    queryKey: ["me", "permisos", suplantacion.suplantacionId ?? "propia"],
    queryFn: async () =>
      (await api.get<{ permisos?: string[] }>("/api/me")).data.permisos ?? [],
    enabled: !!auth.user?.access_token,
    staleTime: 60_000,
  });
  return query.data ?? [];
}

/** True si el usuario tiene el permiso funcional indicado. */
export function useTienePermiso(codigo: string): boolean {
  return usePermisos().includes(codigo);
}

/**
 * Rol de administrador del token REAL, sin importar a quién se esté
 * suplantando. Es el que decide si se puede iniciar una suplantación: mientras
 * un administrador navega como otra persona no debe poder abrir otra sesión
 * —el backend lo rechaza— y tampoco debe ofrecérsele.
 */
export function useEsAdminReal(): boolean {
  const auth = useAuth();
  return rolesDelToken(auth.user?.access_token).includes("ADMIN_SICEN");
}

/**
 * Roles operativos internos de NPMX. Espejo de
 * {@code IamApi.ROLES_INTERNOS_NPMX} en el backend: mantener sincronizadas.
 *
 * Una cuenta es de NPMX o de una empresa, nunca de las dos, así que no tener
 * ninguno de estos roles equivale a ser usuario externo (cliente o gestoría).
 */
export const ROLES_INTERNOS_NPMX = [
  "ADMIN_SICEN",
  "GERENTE_CALIDAD",
  "AUXILIAR_CALIDAD",
  "RESPONSABLE_TELECOM",
  "RESPONSABLE_ELECTRICA",
  "RESPONSABLE_EFICIENCIA",
  "RESPONSABLE_CERTIFICADOS",
  "EVALUADOR_TECNICO",
  "COORDINADOR_TECNICO",
  "AUDITOR_INTERNO",
  "DIRECCION",
  "VIGILANCIA",
  "FACTURACION",
] as const;

/**
 * True si el usuario es personal de NPMX. Respeta la suplantación —un
 * administrador navegando como gestor cuenta como externo—, que es justo lo que
 * se necesita para no enseñarle jerga interna a quien no lo es.
 */
export function useEsInternoNpmx(): boolean {
  return useTieneRol(...ROLES_INTERNOS_NPMX);
}
